/** Anything with a millisecond offset from the timeline's own start - production's `CandidateLayer` timelines and the QA lab's own sequences both satisfy this structurally, so one scheduler serves both without either depending on the other's shape. */
export interface TimedItem {
  readonly atMs: number;
}

export type StepCallback<T> = (step: T, index: number, total: number) => void;

/**
 * Promoted from `src/audio/lab/SequenceScheduler.ts`. Framework-free step
 * scheduler for any `atMs`-timed layer list - production's `AudioManager`
 * (scheduling a locked recipe's multi-layer moment) and the QA lab's
 * audition sequences both use it. Injectable timer functions so it's
 * fully unit-testable (with `vi.useFakeTimers()`, or a hand-rolled fake)
 * without any real Web Audio or real elapsed time.
 *
 * Deliberately dumb: it only knows about millisecond offsets and step
 * callbacks, never about recipes/Web Audio - that split is what makes "a
 * new sequence cancels the prior one" and "stop cancels every scheduled
 * step" plain, direct assertions instead of requiring a mocked
 * AudioContext. `play()` is generic per call (not the class itself) so
 * the SAME instance can schedule different item shapes across calls, with
 * each call's item type inferred independently - the one thing a
 * class-level type parameter cannot do, since TypeScript fixes a class's
 * type argument at construction, not per later method call.
 */
export class SequenceScheduler {
  private timerIds: Array<ReturnType<typeof setTimeout>> = [];
  private sequenceToken = 0;

  constructor(
    private readonly setTimer: (fn: () => void, ms: number) => ReturnType<typeof setTimeout> = (fn, ms) => setTimeout(fn, ms),
    private readonly clearTimer: (id: ReturnType<typeof setTimeout>) => void = (id) => clearTimeout(id),
  ) {}

  get isPlaying(): boolean {
    return this.timerIds.length > 0;
  }

  get activeTimerCount(): number {
    return this.timerIds.length;
  }

  /**
   * Schedules every step's callback at its own `atMs` offset from now, and
   * `onComplete` (if given) shortly after the last one. Always calls
   * `stop()` first - a new sequence starting mid-playback of another
   * always wins.
   */
  play<T extends TimedItem>(steps: readonly T[], onStep: StepCallback<T>, onComplete?: () => void): void {
    this.stop();
    const token = ++this.sequenceToken;
    for (const [i, step] of steps.entries()) {
      const id = this.setTimer(() => {
        if (token !== this.sequenceToken) return;
        onStep(step, i, steps.length);
      }, step.atMs);
      this.timerIds.push(id);
    }
    if (onComplete) {
      const lastAtMs = steps.reduce((max, s) => Math.max(max, s.atMs), 0);
      const id = this.setTimer(() => {
        if (token === this.sequenceToken) onComplete();
      }, lastAtMs + 50);
      this.timerIds.push(id);
    }
  }

  /** Cancels every scheduled step of the current sequence (if any) - a no-op if nothing is playing. Also invalidates any timer that already fired but is racing this call, via the token check in `play()`. */
  stop(): void {
    this.sequenceToken++;
    for (const id of this.timerIds) this.clearTimer(id);
    this.timerIds = [];
  }
}
